import { fetch } from '@atlassian/eoc-fetch';

import {
	type Contact,
	type ContactsResponse,
	type ContactTypes,
	type OrderContactResponse,
	type VerificationResponse,
} from '../../common/types';
import { getTargetAccountIdSuffix } from '../../common/utils';

export const getContacts = (targetAccountId: string | undefined | null) =>
	fetch<{ contacts: ContactsResponse }>(
		`/v1/users/contact${getTargetAccountIdSuffix(targetAccountId)}`,
	);

export const addContactEndpoint = (
	newAddress: string,
	method: ContactTypes,
	targetAccountId: string | undefined,
) => {
	return fetch<Contact>(`/v1/users/contact${getTargetAccountIdSuffix(targetAccountId)}`, {
		method: 'POST',
		body: { address: newAddress, method: method.toLowerCase() },
	});
};

export const orderContactEndpoint = (
	contactId: string,
	after: string | null,
	targetAccountId: string | undefined,
) => {
	return fetch<OrderContactResponse>(
		`/v1/users/contact/order${getTargetAccountIdSuffix(targetAccountId)}`,
		{
			method: 'PATCH',
			body: {
				contactId,
				after,
			},
		},
	);
};

export const updateContactStatusEndpoint = (status: boolean) => {
	return status ? enableContactEndpoint : disableContactEndpoint;
};

const enableContactEndpoint = (contactId: string, targetAccountId: string | undefined) => {
	return fetch<Contact>(
		`/v1/users/contact/state/enabled${getTargetAccountIdSuffix(targetAccountId)}`,
		{
			method: 'PATCH',
			body: { contactId },
		},
	);
};

const disableContactEndpoint = (contactId: string, targetAccountId: string | undefined) => {
	return fetch<Contact>(
		`/v1/users/contact/state/disabled${getTargetAccountIdSuffix(targetAccountId)}`,
		{
			method: 'PATCH',
			body: { contactId },
		},
	);
};

export const updateContactVisibleEndpoint = (visible: boolean) => {
	return visible ? setContactVisibleEndpoint : setContactHiddenEndpoint;
};

const setContactVisibleEndpoint = (contactId: string) => {
	return fetch<Contact>(`/v1/users/contact/state/visible`, {
		method: 'PATCH',
		body: { contactId },
	});
};

const setContactHiddenEndpoint = (contactId: string) => {
	return fetch<Contact>(`/v1/users/contact/state/hidden`, {
		method: 'PATCH',
		body: { contactId },
	});
};

export const setContactPrimaryEndpoint = (contactId: string) => {
	return fetch<Contact>(`/v1/users/contact/state/primary`, {
		method: 'PATCH',
		body: { contactId },
	});
};

export const deleteContactEndpoint = (contactId: string, targetAccountId: string | undefined) => {
	return fetch(`/v1/users/contact${getTargetAccountIdSuffix(targetAccountId)}`, {
		method: 'DELETE',
		body: { contactId },
		responseType: 'headers',
	});
};

export const editContactEndpoint = (
	contactId: string,
	newAddress: string,
	targetAccountId: string | undefined,
) => {
	return fetch<Contact>(`/v1/users/contact${getTargetAccountIdSuffix(targetAccountId)}`, {
		method: 'PATCH',
		body: { contactId, address: newAddress },
	});
};

export const sendVerificationCodeEndpoint = (
	phoneNumber: string,
	method: string,
	targetAccountId: string | undefined,
) => {
	return fetch<VerificationResponse>(
		`/v1/users/contact/verification${getTargetAccountIdSuffix(targetAccountId)}`,
		{
			method: 'POST',
			body: {
				phoneNumber,
				method: method.toLowerCase(),
			},
		},
	);
};

export const checkVerificationCodeEndpoint = (
	phoneNumber: string,
	code: string,
	targetAccountId: string | undefined,
) => {
	return fetch(`/v1/users/contact/verification/check${getTargetAccountIdSuffix(targetAccountId)}`, {
		method: 'POST',
		body: {
			phoneNumber,
			code,
		},
		responseType: 'headers',
	});
};
